import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Bold, Italic, Heading2, Link as LinkIcon, List as ListIcon, Image as ImageIcon, Eye } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import { blogAdminAPI, programsAdminAPI } from '../../services/api';
import '../../styles/admin-shared.css';

const STATUS_OPTIONS = [
  { value: 'DRAFT',     label: 'Rascunho' },
  { value: 'SCHEDULED', label: 'Agendado' },
  { value: 'PUBLISHED', label: 'Publicado' },
  { value: 'ARCHIVED',  label: 'Arquivado' },
];

const slugify = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');

const EMPTY = {
  title: '', slug: '', excerpt: '', content: '', cover_image: '',
  status: 'DRAFT', published_at: '',
  seo_title: '', seo_description: '', seo_keywords: '', canonical_url: '', og_image: '',
  author_id: '', category_id: '', program_id: '',
  tag_ids: [],
};

const BlogEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  useEffect(() => {
    (async () => {
      try {
        const [a, c, t, p] = await Promise.all([
          blogAdminAPI.listAuthors().catch(() => ({ data: { data: [] } })),
          blogAdminAPI.listCategories().catch(() => ({ data: { data: [] } })),
          blogAdminAPI.listTags().catch(() => ({ data: { data: [] } })),
          programsAdminAPI.list().catch(() => ({ data: { data: [] } })),
        ]);
        setAuthors(a.data.data || []);
        setCategories(c.data.data || []);
        setTags(t.data.data || []);
        setPrograms(p.data.data || []);

        if (isEdit) {
          const { data } = await blogAdminAPI.getPost(id);
          const post = data.data;
          setForm({
            ...EMPTY,
            ...post,
            published_at: post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : '',
            tag_ids: (post.tags || []).map((tg) => tg.id),
            author_id:   post.author_id   || '',
            category_id: post.category_id || '',
            program_id:  post.program_id  || '',
            excerpt: post.excerpt || '',
            cover_image: post.cover_image || '',
            seo_title: post.seo_title || '',
            seo_description: post.seo_description || '',
            seo_keywords: post.seo_keywords || '',
            canonical_url: post.canonical_url || '',
            og_image: post.og_image || '',
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      alert('Título e conteúdo são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug ? slugify(form.slug) : slugify(form.title),
        published_at: form.published_at ? new Date(form.published_at).toISOString() : null,
        author_id:   form.author_id   || null,
        category_id: form.category_id || null,
        program_id:  form.program_id  || null,
      };
      if (isEdit) await blogAdminAPI.updatePost(id, payload);
      else        await blogAdminAPI.createPost(payload);
      navigate('/admin/blog');
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  // Toolbar helpers — wrap selected text in textarea with markers.
  const wrap = (before, after = before) => {
    const el = document.getElementById('post-content');
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const txt   = form.content;
    const next  = txt.slice(0, start) + before + txt.slice(start, end) + after + txt.slice(end);
    setForm({ ...form, content: next });
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd   = end + before.length;
    }, 0);
  };

  const toggleTag = (tagId) => {
    const next = form.tag_ids.includes(tagId)
      ? form.tag_ids.filter((x) => x !== tagId)
      : [...form.tag_ids, tagId];
    setForm({ ...form, tag_ids: next });
  };

  // Live SEO/social preview values
  const previewTitle = form.seo_title || form.title || 'Título do post';
  const previewDesc  = form.seo_description || form.excerpt || 'Descrição aparecerá aqui…';
  const previewUrl   = `lifyup.com.br/blog/${form.slug || slugify(form.title) || 'post'}`;
  const previewImg   = form.og_image || form.cover_image;

  const charCount = useMemo(() => ({
    title:   previewTitle.length,
    desc:    previewDesc.length,
  }), [previewTitle, previewDesc]);

  if (loading) return <AdminLayout><div>Carregando…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <button className="btn-link" onClick={() => navigate('/admin/blog')}><ArrowLeft size={16} /> Voltar</button>
            <h1>{isEdit ? 'Editar post' : 'Novo post'}</h1>
          </div>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          {/* COLUNA PRINCIPAL */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AdminCard>
              <div className="form-row">
                <label>Título *</label>
                <input value={form.title} onChange={setF('title')} placeholder="Título do post" style={{ fontSize: 18 }} />
              </div>
              <div className="form-row">
                <label>Slug</label>
                <input value={form.slug} onChange={setF('slug')} placeholder={slugify(form.title) || 'slug-do-post'} />
              </div>
              <div className="form-row">
                <label>Resumo (excerpt)</label>
                <textarea rows={2} value={form.excerpt} onChange={setF('excerpt')} placeholder="Exibido em listagens e meta description fallback." />
              </div>
            </AdminCard>

            <AdminCard>
              <label style={{ display: 'block', marginBottom: 8 }}>Conteúdo *</label>
              <div className="editor-toolbar" style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                <button type="button" className="btn-icon" onClick={() => wrap('**')} title="Negrito"><Bold size={16} /></button>
                <button type="button" className="btn-icon" onClick={() => wrap('*')}  title="Itálico"><Italic size={16} /></button>
                <button type="button" className="btn-icon" onClick={() => wrap('\n## ', '')} title="Heading 2"><Heading2 size={16} /></button>
                <button type="button" className="btn-icon" onClick={() => wrap('[', '](https://)')} title="Link"><LinkIcon size={16} /></button>
                <button type="button" className="btn-icon" onClick={() => wrap('\n- ', '')} title="Lista"><ListIcon size={16} /></button>
                <button type="button" className="btn-icon" onClick={() => wrap('\n![alt](', ')')} title="Imagem"><ImageIcon size={16} /></button>
              </div>
              <textarea
                id="post-content"
                rows={20}
                value={form.content}
                onChange={setF('content')}
                placeholder="Escreva em Markdown ou HTML…"
                style={{ width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 14, lineHeight: 1.6 }}
              />
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}>SEO</h3>
              <div className="form-row">
                <label>SEO Title <small style={{ opacity: 0.6 }}>({charCount.title}/60)</small></label>
                <input value={form.seo_title} onChange={setF('seo_title')} maxLength={70} placeholder={form.title} />
              </div>
              <div className="form-row">
                <label>SEO Description <small style={{ opacity: 0.6 }}>({charCount.desc}/160)</small></label>
                <textarea rows={2} value={form.seo_description} onChange={setF('seo_description')} maxLength={200} placeholder={form.excerpt} />
              </div>
              <div className="form-row">
                <label>Keywords (separadas por vírgula)</label>
                <input value={form.seo_keywords} onChange={setF('seo_keywords')} />
              </div>
              <div className="form-row">
                <label>Canonical URL</label>
                <input value={form.canonical_url} onChange={setF('canonical_url')} placeholder={`https://lifyup.com.br/blog/${form.slug || 'post'}`} />
              </div>
              <div className="form-row">
                <label>OG Image URL</label>
                <input value={form.og_image} onChange={setF('og_image')} placeholder={form.cover_image} />
              </div>
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}><Eye size={16} /> Preview social</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  {previewImg && <img src={previewImg} alt="" style={{ width: '100%', height: 140, objectFit: 'cover' }} />}
                  <div style={{ padding: 12 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>lifyup.com.br</div>
                    <div style={{ fontWeight: 600, fontSize: 14, marginTop: 4 }}>{previewTitle}</div>
                    <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4 }}>{previewDesc}</div>
                  </div>
                </div>
                <div>
                  <div style={{ color: '#1a0dab', fontSize: 18, fontWeight: 500 }}>{previewTitle}</div>
                  <div style={{ color: '#006621', fontSize: 13, marginTop: 2 }}>{previewUrl}</div>
                  <div style={{ color: '#545454', fontSize: 13, marginTop: 4 }}>{previewDesc}</div>
                </div>
              </div>
            </AdminCard>
          </div>

          {/* COLUNA LATERAL */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Publicação</h3>
              <div className="form-row">
                <label>Status</label>
                <select value={form.status} onChange={setF('status')}>
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label>Data de publicação</label>
                <input type="datetime-local" value={form.published_at} onChange={setF('published_at')} />
              </div>
              <div className="form-row">
                <label>Capa (URL)</label>
                <input value={form.cover_image} onChange={setF('cover_image')} placeholder="https://…" />
                {form.cover_image && <img src={form.cover_image} alt="" style={{ marginTop: 8, width: '100%', borderRadius: 6 }} />}
              </div>
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Organização</h3>
              <div className="form-row">
                <label>Autor</label>
                <select value={form.author_id} onChange={setF('author_id')}>
                  <option value="">— Sem autor —</option>
                  {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label>Categoria</label>
                <select value={form.category_id} onChange={setF('category_id')}>
                  <option value="">— Sem categoria —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label>Programa</label>
                <select value={form.program_id} onChange={setF('program_id')}>
                  <option value="">— Geral (todos) —</option>
                  {programs.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label>Tags</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {tags.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => toggleTag(t.id)}
                      className={`tag-chip ${form.tag_ids.includes(t.id) ? 'active' : ''}`}
                      style={{
                        padding: '4px 10px', borderRadius: 12, border: '1px solid #d1d5db',
                        background: form.tag_ids.includes(t.id) ? '#1A95FF' : 'transparent',
                        color: form.tag_ids.includes(t.id) ? '#fff' : 'inherit',
                        fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      {t.name}
                    </button>
                  ))}
                  {tags.length === 0 && <small style={{ opacity: 0.6 }}>Crie tags na aba &quot;Tags&quot;.</small>}
                </div>
              </div>
            </AdminCard>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BlogEditPage;
